import React from 'react'
import {
    Text,
    Box,
    Divider,
    Tooltip as ChakraUITooltip,
    IconButton,

    /* Hooks */
    useMultiStyleConfig
} from '@chakra-ui/react'
import PropTypes from 'prop-types'

/* Utils */
import {renderComponentsSwitch} from '../../utils/contentRenderer'
import {generateStyles, getHideStyles} from '../../utils/utils'

/* Icons */
import {InfoCircle} from '../../../icons'

const CategoryBannerBlock = ({
    PreTitle,
    CategoryBannerBlockPreTitleStyle,
    Title,
    CategoryBannerBlockTitleStyle,
    Description,
    CategoryBannerBlockDescriptionStyle,
    Disclaimer,
    CategoryBannerBlockDisclaimerStyle,
    Tooltip,
    CategoryBannerBlockTooltipStyle,
    BannerCTA,
    ResponsiveBackgroundMedia,
    overwriteContainerStyling,
    overwriteWrapperStyling,
    overwriteTitleStyling,
    overwriteCtaWrapperStyling,
    BannerViewMode,
    BackgroundColorPicker,
    BackgroundColor,
    HideOnDevices,
    ...props
}) => {
    const hideStyles = getHideStyles(HideOnDevices)
    if (hideStyles.doNotLoadComponent) return

    // Dynamic BannerViewMode fallback based on image/color configuration
    const computedBannerViewMode =
        BannerViewMode ||
        (!ResponsiveBackgroundMedia
            ? 'Background Color'
            : BackgroundColorPicker
            ? 'Split Image And Color'
            : 'Background Image')

    const global = useMultiStyleConfig('GlobalCMS')
    const styles = useMultiStyleConfig('CategoryBannerBlock', {
        ...props,
        BackgroundColorPicker: BackgroundColorPicker,
        BackgroundColor: BackgroundColor,
        ResponsiveBackgroundMedia: ResponsiveBackgroundMedia,
        BannerViewMode: computedBannerViewMode
    })

    const renderImage = () => {
        if (!ResponsiveBackgroundMedia) return null

        return (
            <Box sx={styles.imageWrapper}>
                {renderComponentsSwitch(ResponsiveBackgroundMedia?.Expanded)}
            </Box>
        )
    }

    return (
        <Box
            {...global.notFullBleed}
            {...styles.container}
            {...overwriteContainerStyling}
            {...hideStyles}
            data-block-type="CategoryBannerBlock"
        >
            <Box {...styles.wrapper} {...overwriteWrapperStyling}>
                {/* Render image first for Background Color mode */}
                {computedBannerViewMode === 'Background Color' && renderImage()}

                <Box {...styles.contentWrapper}>
                    {PreTitle && PreTitle !== '' && (
                        <Text
                            {...generateStyles(
                                CategoryBannerBlockPreTitleStyle,
                                {...styles.preTitle},
                                {
                                    h1: {base: 'md', lg: 'xl'}
                                }
                            )}
                        >
                            {PreTitle}
                        </Text>
                    )}

                    {Title && Title !== '' && (
                        <Text
                            {...generateStyles(CategoryBannerBlockTitleStyle, {
                                ...styles.title,
                                ...overwriteTitleStyling
                            })}
                        >
                            {Title}
                        </Text>
                    )}

                    {Description && Description !== '' && (
                        <Text
                            {...generateStyles(CategoryBannerBlockDescriptionStyle, {
                                ...styles.description
                            })}
                        >
                            {Description}
                        </Text>
                    )}

                    {Disclaimer && Disclaimer !== '' && (
                        <Text
                            {...generateStyles(CategoryBannerBlockDisclaimerStyle, {
                                ...styles.disclaimer
                            })}
                        >
                            {Disclaimer}
                        </Text>
                    )}

                    {Tooltip && Tooltip !== '' && (
                        <ChakraUITooltip
                            closeOnClick={false}
                            label={Tooltip}
                            placement="bottom-end"
                            {...generateStyles(CategoryBannerBlockTooltipStyle, {
                                ...global.tooltip
                            })}
                        >
                            <IconButton {...global.tooltipIcon} icon={<InfoCircle />} />
                        </ChakraUITooltip>
                    )}
                </Box>
                {BannerCTA && BannerCTA.length > 0 && (
                    <Box {...styles.ctaWrapper} {...overwriteCtaWrapperStyling}>
                        {BannerCTA.map((cta, index) => {
                            return (
                                <React.Fragment key={`banner-cta-${index}`}>
                                    {index > 0 &&
                                        cta?.ContentLink?.Expanded?.Styling === 'Text' && (
                                            <Divider
                                                orientation="vertical"
                                                {...styles.dividerStyling}
                                            />
                                        )}
                                    <Box>
                                        {renderComponentsSwitch(cta?.ContentLink?.Expanded, {
                                            ContainerCustomStyling: {
                                                lineHeight: '100%'
                                            },
                                            stylingTextOverwrite: {
                                                fontSize: {base: 'sm'},
                                                lineHeight: {base: '100%'}
                                            }
                                        })}
                                    </Box>
                                </React.Fragment>
                            )
                        })}
                    </Box>
                )}
            </Box>
            {/* Render image outside wrapper for Split Image and Background Image modes */}
            {computedBannerViewMode !== 'Background Color' && renderImage()}
        </Box>
    )
}

CategoryBannerBlock.propTypes = {
    Title: PropTypes.string,
    CategoryBannerBlockTitleStyle: PropTypes.object,
    Description: PropTypes.string,
    CategoryBannerBlockDescriptionStyle: PropTypes.object,
    Disclaimer: PropTypes.string,
    CategoryBannerBlockDisclaimerStyle: PropTypes.object,
    BannerCTA: PropTypes.array,
    BackgroundColorPicker: PropTypes.string,
    BackgroundColor: PropTypes.string,
    BannerViewMode: PropTypes.string,
    ResponsiveBackgroundMedia: PropTypes.object
}

export default CategoryBannerBlock
